// Regras puras da fila de saques em USDT (`spec/30-scalefy.md` §5.7): rótulos, formato de
// `txid`, formato de USDT e quem pode o quê. Sem rede e sem React, pro teste ser direto.

import type { Tone } from '../../components/kit/ink.ts';
import type { CryptoNetwork, IntlStatus, IntlTotals, IntlWithdrawal } from './queries.ts';

/** Ordem das abas: a fila primeiro, depois o que está na mão de alguém, depois o histórico. */
export const STATUS_IDS = [
  'requested',
  'processing',
  'paid',
  'rejected',
  'cancelled',
] as const satisfies readonly IntlStatus[];

export const QUEUE_STATUS: IntlStatus = 'requested';

export const STATUS_META: Record<IntlStatus, { label: string; tab: string; tone: Tone }> = {
  requested: { label: 'Na fila', tab: 'Na fila', tone: 'wait' },
  processing: { label: 'Em pagamento', tab: 'Em pagamento', tone: 'info' },
  paid: { label: 'Pago', tab: 'Pagos', tone: 'ok' },
  rejected: { label: 'Rejeitado', tab: 'Rejeitados', tone: 'bad' },
  cancelled: { label: 'Cancelado', tab: 'Cancelados', tone: 'neutral' },
};

/**
 * `txid`: mesmas expressões de `validateTxid` (`packages/domain/src/crypto-address.ts`).
 * O painel não importa `@leztypay/domain` em runtime; se o formato mudar lá, muda aqui.
 * O servidor valida de novo (`400 invalid_txid`).
 */
const TXID: Record<CryptoNetwork, RegExp> = {
  tron: /^[0-9a-fA-F]{64}$/,
  bsc: /^0x[0-9a-fA-F]{64}$/,
};

export const NETWORK: Record<
  CryptoNetwork,
  { name: string; label: string; txidFormat: string; txidLength: number; explorer: string }
> = {
  tron: {
    name: 'Tron',
    label: 'Tron (TRC20)',
    txidFormat: '64 caracteres de 0 a 9 e de a até f, sem 0x na frente',
    txidLength: 64,
    explorer: 'tronscan.org',
  },
  bsc: {
    name: 'BSC',
    label: 'BSC (BEP20)',
    txidFormat: '0x seguido de 64 caracteres de 0 a 9 e de a até f',
    txidLength: 66,
    explorer: 'bscscan.com',
  },
};

export function isTxidFor(network: CryptoNetwork, raw: string): boolean {
  return TXID[network].test(raw.trim());
}

/**
 * O `txid` colado tem o formato da OUTRA rede. É o sinal mais barato de que o envio saiu
 * pela rede errada, e rede errada é dinheiro perdido: a tela avisa antes de qualquer coisa.
 */
export function txidIsFromOtherNetwork(network: CryptoNetwork, raw: string): boolean {
  return isTxidFor(network === 'tron' ? 'bsc' : 'tron', raw);
}

/**
 * Centavos de USDT como decimal exato com PONTO e sem separador de milhar (`2300` vira
 * `23.00`), o mesmo formato de `usdt_net_decimal` da API. Um formato só na tela inteira:
 * o que o admin lê é o que ele digita na carteira. Só inteiro, nunca float.
 */
export function usdtDecimal(cents: number): string {
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError('usdtDecimal espera um inteiro de centavos');
  }
  const digits = String(Math.abs(cents)).padStart(3, '0');
  return `${cents < 0 ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

/**
 * Lê USDT digitado e devolve centavos inteiros. ESTRITO de propósito: só dígitos e, no
 * máximo, um separador (ponto ou vírgula) com uma ou duas casas. `1.000` é recusado em vez
 * de adivinhado: num leitor tolerante ele viraria mil, e quem digitou podia querer um.
 */
export function parseUsdt(input: string): number | undefined {
  const match = /^(\d{1,12})(?:[.,](\d{1,2}))?$/.exec(input.trim());
  if (match === null) {
    return undefined;
  }
  const cents = Number(`${match[1]}${(match[2] ?? '').padEnd(2, '0')}`);
  return Number.isSafeInteger(cents) ? cents : undefined;
}

/** `23.00 USDT`. Recebe a string decimal exata, nunca um número. */
export function usdt(decimal: string): string {
  return `${decimal} USDT`;
}

/** Cotação travada: `5.432100` vira `R$ 5,432100`. Troca de caractere, sem conta. */
export function fxRateLabel(rate: string): string {
  return `R$ ${rate.replace('.', ',')}`;
}

/** `2026-10-07` vira `07/10/2026` sem passar por `Date` (a data é civil, não instante). */
export function civilDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return year === undefined || month === undefined || day === undefined
    ? isoDate
    : `${day}/${month}/${year}`;
}

/** 6 + `...` + 6, igual `maskCryptoAddress` do domínio. Só pra mostrar o que foi colado. */
export function maskAddress(address: string): string {
  const text = address.trim();
  return text.length <= 12 ? text : `${text.slice(0, 6)}...${text.slice(-6)}`;
}

/** O miolo do endereço, entre os 6 primeiros e os 6 últimos que a API destaca. */
export function addressMiddle(withdrawal: IntlWithdrawal): string {
  return withdrawal.address.slice(
    withdrawal.address_prefix.length,
    withdrawal.address.length - withdrawal.address_suffix.length,
  );
}

/** `rejectBodySchema` da API: motivo de 10 a 500 caracteres, mostrado ao lojista. */
export const REASON_MIN_LENGTH = 10;
export const REASON_MAX_LENGTH = 500;

/** `FOREIGN_REJECT_AFTER_MS` do core: outro admin só rejeita 24 h depois da assunção. */
const FOREIGN_REJECT_AFTER_MS = 24 * 60 * 60 * 1000;

export type Ownership = 'unclaimed' | 'mine' | 'other';

/** De quem é o pagamento. Sem saber quem está logado, um pedido assumido conta como alheio. */
export function ownershipOf(
  withdrawal: Pick<IntlWithdrawal, 'claimed_by'>,
  meId: string | undefined,
): Ownership {
  if (withdrawal.claimed_by === null) {
    return 'unclaimed';
  }
  return withdrawal.claimed_by === meId ? 'mine' : 'other';
}

/**
 * Quando um admin que NÃO assumiu passa a poder rejeitar. `undefined` quando a regra não
 * se aplica (não está em pagamento, ou é de quem está logado).
 */
export function foreignRejectOpensAt(
  withdrawal: Pick<IntlWithdrawal, 'status' | 'claimed_by' | 'claimed_at'>,
  meId: string | undefined,
): Date | undefined {
  if (
    withdrawal.status !== 'processing' ||
    withdrawal.claimed_at === null ||
    ownershipOf(withdrawal, meId) !== 'other'
  ) {
    return undefined;
  }
  return new Date(new Date(withdrawal.claimed_at).getTime() + FOREIGN_REJECT_AFTER_MS);
}

/** `true` enquanto a rejeição ainda é só de quem assumiu. */
export function rejectLockedToClaimer(
  withdrawal: Pick<IntlWithdrawal, 'status' | 'claimed_by' | 'claimed_at'>,
  meId: string | undefined,
  now: Date = new Date(),
): boolean {
  const opensAt = foreignRejectOpensAt(withdrawal, meId);
  return opensAt !== undefined && now.getTime() <= opensAt.getTime();
}

/**
 * Padrão de `crypto_processing_alert_hours` (alerta `crypto_withdrawal_processing_stuck`).
 * A tela só usa pra dizer "parado": quem decide o alerta é o servidor.
 */
const STUCK_AFTER_MS = 6 * 60 * 60 * 1000;

export function isStuck(
  withdrawal: Pick<IntlWithdrawal, 'status' | 'claimed_at'>,
  now: Date = new Date(),
): boolean {
  return (
    withdrawal.status === 'processing' &&
    withdrawal.claimed_at !== null &&
    now.getTime() - new Date(withdrawal.claimed_at).getTime() > STUCK_AFTER_MS
  );
}

/** `usr_…` vira nome (`GET /admin/v1/admins`). Sem a lista, quem lê fica com o id cru. */
export function adminNameMap(
  admins: readonly { id: string; name: string }[] | undefined,
): Record<string, string> {
  return Object.fromEntries((admins ?? []).map((admin) => [admin.id, admin.name]));
}

/** Contagem e soma em USDT de um status, de `totals.by_status`. `undefined` = ainda não chegou. */
export function statusTotals(
  totals: IntlTotals | undefined,
  status: IntlStatus,
): { count: number; usdt_net_decimal: string } | undefined {
  return totals?.by_status[status];
}

/** Motivos pelos quais `claim` vai ser recusado, lidos do detalhe antes do clique. */
export function claimBlockers(seller: {
  status: string;
  payout_locked: boolean;
  has_negative_balance: boolean;
}): string[] {
  const out: string[] = [];
  if (seller.payout_locked) {
    out.push('o saque deste lojista está travado');
  }
  if (seller.status !== 'active') {
    out.push('a conta do lojista não está ativa');
  }
  if (seller.has_negative_balance) {
    out.push('o lojista tem saldo negativo');
  }
  return out;
}
